import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { PublishStateModel } from './publish.state.model';
import {
  SetPublishDimensions,
  GetPublishURL,
  GetPublishIFrame,
  GetPublishURLSuccess,
  GetPublishIFrameSuccess,
  GetPublishURLFail,
  GetPublishIFrameFail,
  SetPublishWidgetURL,
  SetPublishing,
  ResetPublish,
  GetPublishImageCode,
  GetPublishImageCodeSuccess,
  GetPublishImageCodeFail,
  SavePublishImage,
  SavePublishImageFail,
  SavePublishImageSuccess,
} from './publish.actions';
import { PublishService, PublishLinkType } from '../publish.service';
import { defaultScreen } from '../data/screens';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { forkJoin } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Injectable } from '@angular/core';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';

const defaultWidgetURL = '/viewer';

const initialState = {
  publishing: false,
  widgetURL: defaultWidgetURL,
  dimensions: defaultScreen.dimensions,
  minDimensions: {
    width: 300,
    height: 400,
  },
};

@State<PublishStateModel>({
  name: 'publish',
  defaults: initialState,
})
@Injectable()
export class PublishState {
  @Selector()
  static getDimensions(state: PublishStateModel) {
    return state.dimensions;
  }

  @Selector()
  static getMinDimensions(state: PublishStateModel) {
    return state.minDimensions;
  }

  @Selector()
  static getWidgetURL(state: PublishStateModel) {
    return state.widgetURL;
  }

  @Selector()
  static getPublishing(state: PublishStateModel) {
    return state.publishing;
  }

  @Action(SetPublishWidgetURL)
  setPublishWidgetURL(context: StateContext<PublishStateModel>, action: SetPublishWidgetURL) {
    context.patchState({
      widgetURL: action.widgetURL || defaultWidgetURL,
    });
  }

  @Action(SetPublishDimensions)
  setPublishDimensions(context: StateContext<PublishStateModel>, action: SetPublishDimensions) {
    context.patchState({
      dimensions: action.dimensions,
    });
  }

  @Action(GetPublishURL)
  getPublishURL(context: StateContext<PublishStateModel>, action: GetPublishURL) {
    const { model, camera, widgetOptions } = action;
    const { widgetURL } = context.getState();
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);
    const app = this.store.selectSnapshot(AuthState.getSelectedUserApp);
    const productAccountId = this.store.selectSnapshot(
      OrganizationsState.getSelectedOrganizationProductAccountId,
    );

    // get url
    this.publishService
      .generateWidgetURL(
        model,
        widgetURL,
        widgetOptions,
        camera,
        PublishLinkType.Single,
        lang,
        app,
        productAccountId,
      )
      .subscribe(
        (url) => {
          context.dispatch(new GetPublishURLSuccess(model, url));
        },
        () => {
          context.dispatch(new GetPublishURLFail(model));
        },
      );
  }

  @Action(GetPublishURLFail)
  getPublishURLFail(context: StateContext<PublishStateModel>, action: GetPublishURLFail) {
    this.snackbar.open('Unable to generate URL.');
  }

  @Action(GetPublishIFrame)
  getPublishIFrame(context: StateContext<PublishStateModel>, action: GetPublishIFrame) {
    const { model, camera, widgetOptions } = action;
    const { widgetURL, dimensions } = context.getState();
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);
    const app = this.store.selectSnapshot(AuthState.getSelectedUserApp);
    const productAccountId = this.store.selectSnapshot(
      OrganizationsState.getSelectedOrganizationProductAccountId,
    );

    // get embed code
    this.publishService
      .generateWidgetEmbed(
        model,
        widgetURL,
        widgetOptions,
        dimensions,
        camera,
        PublishLinkType.Single,
        lang,
        app,
        productAccountId,
      )
      .subscribe(
        (embedCode) => {
          context.dispatch(new GetPublishIFrameSuccess(embedCode));
        },
        () => {
          context.dispatch(new GetPublishIFrameFail());
        },
      );
  }

  @Action(SavePublishImage)
  savePublishImage(
    context: StateContext<PublishStateModel>,
    { imageData, imageType }: SavePublishImage,
  ) {
    this.publishService.saveUserImage(imageData, imageType).subscribe(
      (imageURL) => {
        context.dispatch(new SavePublishImageSuccess(imageURL));
      },
      () => {
        context.dispatch(new SavePublishImageFail());
      },
    );
  }

  @Action(GetPublishImageCode)
  getPublishImage(context: StateContext<PublishStateModel>, action: GetPublishImageCode) {
    const { model, camera, widgetOptions, imageData, fileType } = action;
    const { widgetURL } = context.getState();
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);
    const app = this.store.selectSnapshot(AuthState.getSelectedUserApp);

    forkJoin(
      this.publishService.generateWidgetURL(
        model,
        widgetURL,
        widgetOptions,
        camera,
        PublishLinkType.Single,
        lang,
        app,
      ), // generate widget url
      this.publishService.saveUserImage(imageData, fileType), // generate image url
    ).subscribe(
      ([generatedWidgeURL, generatedImageURL]) => {
        const embed = this.publishService.getImageEmbed(
          model.title,
          generatedWidgeURL,
          generatedImageURL,
        );
        context.dispatch(new GetPublishImageCodeSuccess(embed));
      },
      () => {
        context.dispatch(new GetPublishImageCodeFail());
      },
    );
  }

  @Action(SetPublishing)
  setPublishing(context: StateContext<PublishStateModel>, { publishing }: SetPublishing) {
    context.patchState({
      publishing: publishing,
    });
  }

  @Action(ResetPublish)
  resetPublish(context: StateContext<PublishStateModel>, action: ResetPublish) {
    context.setState(initialState);
  }

  constructor(
    private publishService: PublishService,
    private store: Store,
    private snackbar: MatSnackBar,
  ) {}
}
